import {
  useContext,
  useState,
  useRef,
  useCallback,
  type MouseEvent,
} from 'react';
import { useGT } from 'gt-react';
import { CopyIcon, CheckIcon } from '@phosphor-icons/react';
import { AuthContext } from 'src/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin } from '@/utils/user';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const COPY_FEEDBACK_MS = 1200;

interface CopyIdButtonProps {
  id: string;
  label?: string;
  className?: string;
}

export function CopyIdButton({ id, label, className }: CopyIdButtonProps) {
  const gt = useGT();
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const handleClick = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      // Clipboard API is unavailable on non-secure contexts (HTTP) and some
      // embedded webviews. Optional-chaining the call returns undefined
      // silently when the API is missing — we'd then flash "Copied" without
      // actually having copied. Bail before the success state.
      const write = navigator.clipboard?.writeText.bind(navigator.clipboard);
      if (!write) return;
      try {
        await write(id);
      } catch {
        return;
      }
      setCopied(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
    },
    [id],
  );

  if (!isAdmin(currentUser, viewMode)) return null;

  const ariaLabel =
    label ?? gt('Copy ID', { context: 'copy id button accessible label' });

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={handleClick}
            aria-label={ariaLabel}
            className={cn(
              'inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors',
              className,
            )}
          >
            {copied ? (
              <CheckIcon className="h-3.5 w-3.5 text-green-600" />
            ) : (
              <CopyIcon className="h-3.5 w-3.5" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" align="center">
          {label && (
            <div className="text-xs font-medium">
              {copied
                ? gt('Copied', { context: 'copy id button copied label' })
                : label}
            </div>
          )}
          <div className="font-mono text-[11px] opacity-80">{id}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
